import { useState, useEffect } from 'react';
import {
  CreditCard,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { adminApi } from '../../services/api';

interface StripeSettingsData {
  hasSecretKey: boolean;
  hasWebhookSecret: boolean;
  publishableKey: string;
  enabled: boolean;
}

export default function StripeSettingsPage() {
  const [settings, setSettings] = useState<StripeSettingsData | null>(null);
  const [envConfigured, setEnvConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message?: string; error?: string } | null>(null);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    secretKey: '',
    webhookSecret: '',
    publishableKey: '',
    enabled: true,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getStripeSettings();
      const data = res.data;

      setEnvConfigured(data.envConfigured);

      if (data.settings) {
        setSettings(data.settings);
        setFormData({
          secretKey: '',
          webhookSecret: '',
          publishableKey: data.settings.publishableKey || '',
          enabled: data.settings.enabled,
        });
      }
    } catch (error) {
      console.error('Erreur chargement paramètres Stripe:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateStripeSettings({
        secretKey: formData.secretKey || undefined,
        webhookSecret: formData.webhookSecret || undefined,
        publishableKey: formData.publishableKey,
        enabled: formData.enabled,
      });

      setSaveMessage({ type: 'success', text: 'Paramètres Stripe sauvegardés avec succès' });
      setFormData((prev) => ({ ...prev, secretKey: '', webhookSecret: '' }));
      loadSettings();
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Erreur lors de la sauvegarde';
      setSaveMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const res = await adminApi.testStripeConnection({
        secretKey: formData.secretKey || undefined,
      });
      setTestResult(res.data);
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuration Stripe</h1>
        <p className="text-gray-500">
          Configurez les clés Stripe pour activer le paiement en ligne
        </p>
      </div>

      {/* Avertissement env vars */}
      {envConfigured && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">
                Variables d'environnement détectées
              </p>
              <p className="text-sm text-blue-700 mt-1">
                STRIPE_SECRET_KEY est défini dans les variables d'environnement.
                Les paramètres ci-dessous permettent de les remplacer via la base de données.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statut actuel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Statut</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            {(settings?.hasSecretKey || envConfigured) ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-400" />
            )}
            <div>
              <p className="text-sm font-medium text-gray-900">Clé secrète</p>
              <p className="text-xs text-gray-500">
                {envConfigured ? 'Via env' : settings?.hasSecretKey ? 'Configurée' : 'Non configurée'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(settings?.hasWebhookSecret || !!process.env.STRIPE_WEBHOOK_SECRET) ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
            )}
            <div>
              <p className="text-sm font-medium text-gray-900">Webhook Secret</p>
              <p className="text-xs text-gray-500">
                {settings?.hasWebhookSecret ? 'Configuré' : 'Optionnel'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {settings?.enabled !== false ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-400" />
            )}
            <div>
              <p className="text-sm font-medium text-gray-900">Paiements</p>
              <p className="text-xs text-gray-500">
                {settings?.enabled !== false ? 'Activés' : 'Désactivés'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="h-6 w-6 text-purple-500" />
          <h2 className="text-lg font-semibold text-gray-900">Clés API Stripe</h2>
        </div>

        <p className="text-sm text-gray-500">
          Récupérez vos clés dans le{' '}
          <a
            href="https://dashboard.stripe.com/apikeys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-1"
          >
            dashboard Stripe <ExternalLink className="h-3 w-3" />
          </a>
        </p>

        {/* Clé secrète */}
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">
            Clé secrète (Secret Key)
          </label>
          <div className="relative">
            <input
              type={showSecretKey ? 'text' : 'password'}
              value={formData.secretKey}
              onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })}
              className="w-full px-4 py-2 pr-10 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              placeholder={settings?.hasSecretKey ? '••••••••••••••••••••••••' : 'sk_live_...'}
            />
            <button
              type="button"
              onClick={() => setShowSecretKey(!showSecretKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Commence par sk_live_ (production) ou sk_test_ (test)
          </p>
        </div>

        {/* Webhook secret */}
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">
            Webhook Secret
          </label>
          <div className="relative">
            <input
              type={showWebhookSecret ? 'text' : 'password'}
              value={formData.webhookSecret}
              onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
              className="w-full px-4 py-2 pr-10 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              placeholder={settings?.hasWebhookSecret ? '••••••••••••••••••••••••' : 'whsec_...'}
            />
            <button
              type="button"
              onClick={() => setShowWebhookSecret(!showWebhookSecret)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Créez un webhook dans Stripe pointant vers : <code className="bg-gray-100 px-1 rounded">{window.location.origin}/api/billing/webhook</code>
          </p>
        </div>

        {/* Clé publique */}
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">
            Clé publique (Publishable Key) - optionnel
          </label>
          <input
            type="text"
            value={formData.publishableKey}
            onChange={(e) => setFormData({ ...formData, publishableKey: e.target.value })}
            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            placeholder="pk_live_..."
          />
        </div>

        {/* Activer/Désactiver */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="stripeEnabled"
            checked={formData.enabled}
            onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="stripeEnabled" className="text-gray-700">
            Activer les paiements Stripe
          </label>
        </div>

        {/* Messages */}
        {saveMessage && (
          <div
            className={`p-3 rounded-lg text-sm ${
              saveMessage.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {saveMessage.text}
          </div>
        )}

        {testResult && (
          <div
            className={`p-3 rounded-lg text-sm ${
              testResult.success
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {testResult.success ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                {testResult.message}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                {testResult.error}
              </div>
            )}
          </div>
        )}

        {/* Boutons */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing}
            className="btn-secondary flex items-center gap-2"
          >
            {testing ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Tester la connexion
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Sauvegarder
          </button>
        </div>
      </form>

      {/* Guide de configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Guide de configuration</h2>
        <ol className="space-y-3 text-sm text-gray-600 list-decimal list-inside">
          <li>
            Créez un compte sur{' '}
            <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
              dashboard.stripe.com
            </a>
          </li>
          <li>
            Copiez votre <strong>clé secrète</strong> depuis{' '}
            <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
              Developers &gt; API keys
            </a>
          </li>
          <li>
            Créez vos <strong>produits et prix</strong> dans{' '}
            <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
              Products
            </a>{' '}
            (prix récurrent annuel par tracker)
          </li>
          <li>
            Copiez les <strong>Price ID</strong> (ex: price_xxx) dans chaque plan via la{' '}
            <a href="/admin/plans" className="text-cyan-600 hover:underline">page des plans</a>
          </li>
          <li>
            Configurez un <strong>webhook</strong> pointant vers <code className="bg-gray-100 px-1 rounded">{window.location.origin}/api/billing/webhook</code>{' '}
            avec les événements : checkout.session.completed, customer.subscription.*, invoice.*
          </li>
          <li>Copiez le <strong>Webhook Secret</strong> (whsec_xxx) ci-dessus</li>
        </ol>
      </div>
    </div>
  );
}
